import React, { useState, forwardRef, useImperativeHandle } from 'react';
import {
  Flex,
  Box,
  Table,
  Tbody,
  Text,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import {
  createColumnHelper,
} from '@tanstack/react-table';
import PageTable from 'components/layout/PageTable';
import AdminTableCell from 'components/layout/AdminTableCell';
import SortableTableHeader from 'components/layout/SortableTableHeader';
import { useSortableAdminTable } from 'hooks/useSortableAdminTable';
import { getSortableColumnOptions } from 'utils/tableColumnOptions';
import TablePagination from 'components/layout/TablePagination';
import WarrantyEditModal from './WarrantyEditModal';
import ConfirmModal from '../modal/ConfirmModal';
import {
  getWarrantyTypeLabel,
  DEFAULT_WARRANTY_STATUS,
} from '../../utils/warrantyTypes';
import { deleteData, getData, postData } from '../../utils/httpClient';
import authService from 'services/authService';

const columnHelper = createColumnHelper();

const WarrantyTable = forwardRef(function WarrantyTable({
  columnsData,
  data,
  page,
  setPage,
  totalPages,
  pageSize,
  setPageSize,
  setData,
  handleUpdateWarranty,
  handleCreateWarranty,
  onValidateCampaigns,
  sorting,
  onSortingChange,
}, ref) {
  const toast = useToast();
  const [printingKey, setPrintingKey] = useState(null);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const columns = React.useMemo(
    () => createColumns(columnsData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnsData, printingKey],
  );

  const [editModalState, setEditModalState] = React.useState({ isOpen: false });
  const [selectedVehicleService, setSelectedVehicleService] =
    React.useState(null);

      const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
    
  // Exposer funciones al componente padre a través de ref
  useImperativeHandle(ref, () => ({
    openEditModalDirectly: () => {
      // Usa el servicio ya seleccionado en selectedVehicleService
      if (selectedVehicleService) {
        if (!selectedVehicleService?.warranties) {
          handleCreateWarranty(selectedVehicleService);
        }
        setEditModalState({ isOpen: true });
      }
    }
  }));

  const table = useSortableAdminTable({
    data,
    columns,
    sorting,
    onSortingChange,
  });

  const openPdfByDocumentId = async (documentId) => {
    if (!documentId) return;
    const response = await getData('/document/' + documentId);
    const document = response?.payload?.document;
    if (document?.file) {
      const pdfData = new Uint8Array(document.file.data);
      const blob = new Blob([pdfData], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    }
  };

  const printWarrantyDocument = async (rowData, kind) => {
    const serviceId = rowData?.id;
    if (!serviceId) {
      toast({
        title: 'Documento no disponible',
        description: 'No se encontró el servicio de garantía.',
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    const endpoint =
      kind === 'reception'
        ? `/vehicleServices/${serviceId}/reception-pdf`
        : `/vehicleServices/${serviceId}/warranty-pdf`;
    const key = `${kind}-${serviceId}`;
    setPrintingKey(key);
    try {
      const response = await postData(endpoint, {});
      const docId =
        response?.payload?.documentId ||
        response?.payload?.vehicleService?.documentId;
      if (!docId) {
        throw new Error('No se obtuvo el documento generado.');
      }
      await openPdfByDocumentId(docId);
    } catch (err) {
      toast({
        title:
          kind === 'reception'
            ? 'No se pudo imprimir la recepción'
            : 'No se pudo imprimir la garantía finalizada',
        description:
          err?.response?.data?.message ||
          err?.message ||
          'Intentá nuevamente.',
        status: 'error',
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setPrintingKey(null);
    }
  };

  const openEditModal = async (rowData) => {
    // Validar campañas del vehículo antes de abrir el modal de edición
    if (onValidateCampaigns && rowData.vehicle?.chassisNumber) {
      // Guardar el servicio antes de validar campañas
      setSelectedVehicleService(rowData);
      
      const hasPendingCampaigns = await onValidateCampaigns(rowData.vehicle.chassisNumber);
      
      // Si hay campañas pendientes, el modal de edición se abrirá después de cerrar el modal de campañas
      if (hasPendingCampaigns) {
        return;
      }
    }
    
    // Si no hay campañas pendientes, abrir directamente el modal de edición
    setSelectedVehicleService(rowData);
    if (!rowData?.warranties) {
      handleCreateWarranty(rowData);
    }
    setEditModalState({ isOpen: true });
  };


  const handleAttemptClose = () => {
    setIsConfirmCloseOpen(true); // Abre el modal de confirmación
  };

  const confirmCloseEditModal = () => {
    setEditModalState({ isOpen: false });
    setSelectedVehicleService(null);
    setIsConfirmCloseOpen(false); // Cerrar el confirm modal
  };

  const cancelCloseEditModal = () => {
    setIsConfirmCloseOpen(false); // Solo cerrar confirm modal
  };

  const confirmDeleteWarranty = async () => {
    if (!serviceToDelete) return;
    setIsDeleting(true);
    try {
      await deleteData(`/vehicleServices/${serviceToDelete.id}`);
      setData((prev) => prev.filter((item) => item.id !== serviceToDelete.id));
      setServiceToDelete(null);
      toast({
        title: 'Garantía eliminada',
        description: 'La garantía cancelada y su cita fueron eliminadas.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'No se pudo eliminar',
        description: error?.message || 'Intente nuevamente.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };


  function createColumns(columnsData) {
    return columnsData.map((col) =>
      columnHelper.accessor(col.accessor, {
        ...getSortableColumnOptions(col),
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }}>{col.Header}</Text>
        ),
        cell: (info) => {
          const value = info.getValue();

          if (col.accessor === 'customerObservations') {
            return (
              <Text
                fontSize="sm"
                fontWeight="normal"
                whiteSpace="normal"
                wordBreak="break-word"
                overflowWrap="anywhere"
              >
                {value || '—'}
              </Text>
            );
          }

          if (col.accessor === 'warrantyType') {
            const row = info.row.original;
            const code =
              value ||
              row?.warranties?.warrantyType ||
              row?.warranty?.warrantyType ||
              '';
            return (
              <Text fontSize="sm" fontWeight="normal">
                {getWarrantyTypeLabel(code) || '—'}
              </Text>
            );
          }

          if (col.accessor === 'warrantyStatus') {
            const row = info.row.original;
            const serviceCanceled =
              String(row?.status?.name || '').toLowerCase() === 'cancelado';
            const status = serviceCanceled
              ? 'Cancelado'
              : value ||
                row?.warranties?.status ||
                row?.warranty?.status ||
                DEFAULT_WARRANTY_STATUS;
            return (
              <Text fontSize="sm" fontWeight="normal">
                {status || '—'}
              </Text>
            );
          }

          if (col.accessor === 'repairStatus') {
            const row = info.row.original;
            const repairAgenda =
              row?.warranties?.repairAgenda || row?.warranty?.repairAgenda;
            const repairStatus =
              repairAgenda?.vehicleService?.status?.name ||
              repairAgenda?.status ||
              null;
            return (
              <Text
                fontSize="sm"
                fontWeight="normal"
                color={repairStatus ? 'gray.800' : 'gray.400'}
              >
                {repairStatus || 'Sin reparación'}
              </Text>
            );
          }

          // Formato personalizado para campos de fecha
          if (col.accessor === 'date') {
            // Asumiendo value es ISO string con fecha UTC
            const isoDate =
              typeof value === 'string' ? value : value.toISOString();
            // Extraemos la parte YYYY-MM-DD sin zona horaria
            const dateString = isoDate.slice(0, 10);
            // Crear objeto Date solo para formateo (tratamos como local)
            const [year, month, day] = dateString.split('-');
            const formattedDate = `${day}/${month}/${year}`;

            return (
              <Text fontSize="sm" fontWeight="normal">
                {formattedDate}
              </Text>
            );
          }

          // Formato personalizado para campos de hora
          if (col.accessor === 'time') {
            const time = new Date(`1970-01-01T${value}`);
            const formattedTime = time.toLocaleTimeString('es-UY', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            });
            return (
              <Text fontSize="sm" fontWeight="normal">
                {formattedTime}
              </Text>
            );
          }

          // Manejo especial para propiedades anidadas que pueden ser null
          if (col.accessor.includes('.')) {
            const keys = col.accessor.split('.');

            let nestedValue = info.row.original;
            
            for (const key of keys) {
              nestedValue = nestedValue?.[key];
              if (nestedValue === null || nestedValue === undefined) {
                break;
              }
            }
            
            // Para el estado específicamente, mostrar un mensaje más descriptivo
            if (col.accessor === 'status.name' && (!nestedValue || nestedValue === null)) {
              return (
                <Text fontSize="sm" fontWeight="normal" color="gray.400">
                  Sin estado
                </Text>
              );
            }
            
            return (
              <Text fontSize="sm" fontWeight="normal">
                {nestedValue || 'N/A'}
              </Text>
            );
          }

          // Acciones (editar / imprimir)
          if (col.actions) {
            const rowData = info.row.original;
            const statusName = String(
              rowData?.status?.name ||
                rowData?.warranties?.status ||
                rowData?.warranty?.status ||
                '',
            ).toLowerCase();
            const canDelete =
              authService.isAdmin() && statusName === 'cancelado';
            const visibleActions = col.actions.filter(
              (action) => action.type !== 'delete' || canDelete,
            );
            return (
              <Flex align="center">
                {visibleActions.map((action, index) => {
                  const printKind =
                    action.type === 'print-reception'
                      ? 'reception'
                      : action.type === 'print-warranty'
                        ? 'warranty'
                        : null;
                  const isPrinting =
                    printKind &&
                    printingKey === `${printKind}-${rowData?.id}`;

                  return (
                    <Tooltip key={index} label={action.label} hasArrow>
                      <IconButton
                        aria-label={action.label}
                        icon={action.icon}
                        size="sm"
                        variant="ghost"
                        colorScheme={action.colorScheme}
                        ml={2}
                        isLoading={isPrinting}
                        onClick={() => {
                          if (action.type === 'edit') {
                            openEditModal(rowData);
                          } else if (action.type === 'delete') {
                            setServiceToDelete(rowData);
                          } else if (action.type === 'print-reception') {
                            printWarrantyDocument(rowData, 'reception');
                          } else if (action.type === 'print-warranty') {
                            printWarrantyDocument(rowData, 'warranty');
                          }
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Flex>
            );
          }

          // Valor por defecto
          return (
            <Text fontSize="sm" fontWeight="normal">
              {value || 'N/A'}
            </Text>
          );
        },
      }),
    );
  }

  return (
    <>
      <PageTable
        flexDirection="column"
        w="100%"
        px="0px"
       >
        <Box>
          <PageTable.Scroll>
            <Table variant="simple" color="gray.700" mb="24px" mt="12px">
                        <Thead>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                              {headerGroup.headers.map((header) => (
                                <SortableTableHeader
                                  key={header.id}
                                  header={header}
                                  borderColor={borderColor}
                                />
                              ))}
                            </Tr>
                          ))}
                        </Thead>
                        <Tbody>
                          {table.getRowModel().rows.map((row) => (
                            <Tr key={row.id}>
                              {row.getVisibleCells().map((cell) => (
                                <AdminTableCell key={cell.id} cell={cell} />
                              ))}
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
          </PageTable.Scroll>
          <TablePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </Box>
      </PageTable>
      <WarrantyEditModal
        modalState={editModalState}
        closeModal={handleAttemptClose}
        selectedService={selectedVehicleService}
        onSave={handleUpdateWarranty}
      />
        <ConfirmModal
        isOpen={isConfirmCloseOpen}
        onClose={cancelCloseEditModal}
        onConfirm={confirmCloseEditModal}
        header="Cerrar sin guardar"
        message="¿Estás seguro de que querés cerrar el editor? Se perderán los cambios no guardados."
      />
      <ConfirmModal
        isOpen={!!serviceToDelete}
        onClose={() => !isDeleting && setServiceToDelete(null)}
        onConfirm={confirmDeleteWarranty}
        header="Eliminar garantía"
        message="Esta acción eliminará definitivamente la garantía cancelada y su cita."
        confirmLabel="Eliminar"
        confirmVariant="danger"
        isLoading={isDeleting}
      />
    </>
  );
});

export default WarrantyTable;
